import React, { useState, useRef } from "react";
import katex from "katex";
import "katex/dist/katex.min.css";

// ── KaTeX preview ────────────────────────────────────
function Preview({ latex }) {
  if (!latex.trim()) return <span className="ql-mathkb-preview-empty">Aperçu ici…</span>;
  let html = "";
  try {
    html = katex.renderToString(latex, { throwOnError: false, displayMode: true });
  } catch {
    html = `<span style="color:#dc2626;font-size:0.85rem">Syntaxe invalide</span>`;
  }
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

// ── Expression templates ─────────────────────────────
const TEMPLATES = [
  {
    id: "frac",
    label: "Fraction",
    icon: "a/b",
    fields: [
      { id: "num", label: "Numérateur",   placeholder: "ex: a+b" },
      { id: "den", label: "Dénominateur", placeholder: "ex: c" },
    ],
    latex: (v) => `\\dfrac{${v.num||"a"}}{${v.den||"b"}}`,
    preview: (v) => `\\dfrac{${v.num||"a"}}{${v.den||"b"}}`,
  },
  {
    id: "pow",
    label: "Puissance",
    icon: "aⁿ",
    fields: [
      { id: "base", label: "Base",     placeholder: "ex: x" },
      { id: "exp",  label: "Exposant", placeholder: "ex: 2" },
    ],
    latex: (v) => `${v.base||"x"}^{${v.exp||"n"}}`,
  },
  {
    id: "sub",
    label: "Indice",
    icon: "aₙ",
    fields: [
      { id: "base", label: "Base",   placeholder: "ex: x" },
      { id: "sub",  label: "Indice", placeholder: "ex: n" },
    ],
    latex: (v) => `${v.base||"x"}_{${v.sub||"n"}}`,
  },
  {
    id: "sqrt",
    label: "Racine carrée",
    icon: "√x",
    fields: [
      { id: "x", label: "Radicande", placeholder: "ex: 2" },
    ],
    latex: (v) => `\\sqrt{${v.x||"x"}}`,
  },
  {
    id: "nrt",
    label: "Racine n-ième",
    icon: "ⁿ√x",
    fields: [
      { id: "n", label: "Indice",    placeholder: "ex: 3" },
      { id: "x", label: "Radicande", placeholder: "ex: 8" },
    ],
    latex: (v) => `\\sqrt[${v.n||"n"}]{${v.x||"x"}}`,
  },
  {
    id: "log",
    label: "Logarithme",
    icon: "logₐ",
    fields: [
      { id: "base", label: "Base",     placeholder: "ex: 2 (vide = 10)" },
      { id: "x",    label: "Argument", placeholder: "ex: 8" },
    ],
    latex: (v) => v.base ? `\\log_{${v.base}}\\left(${v.x||"x"}\\right)` : `\\log\\left(${v.x||"x"}\\right)`,
  },
  {
    id: "ln",
    label: "Logarithme naturel",
    icon: "ln",
    fields: [
      { id: "x", label: "Argument", placeholder: "ex: x" },
    ],
    latex: (v) => `\\ln\\left(${v.x||"x"}\\right)`,
  },
  {
    id: "exp",
    label: "Exponentielle",
    icon: "eˣ",
    fields: [
      { id: "x", label: "Exposant", placeholder: "ex: x" },
    ],
    latex: (v) => `e^{${v.x||"x"}}`,
  },
  {
    id: "abs",
    label: "Valeur absolue",
    icon: "|x|",
    fields: [
      { id: "x", label: "Expression", placeholder: "ex: x-3" },
    ],
    latex: (v) => `\\left|${v.x||"x"}\\right|`,
  },
  {
    id: "lim",
    label: "Limite",
    icon: "lim",
    fields: [
      { id: "var",  label: "Variable",     placeholder: "ex: x" },
      { id: "to",   label: "Vers",         placeholder: "ex: +\\infty" },
      { id: "expr", label: "Expression",   placeholder: "ex: f(x)" },
    ],
    latex: (v) => `\\lim_{${v.var||"x"} \\to ${v.to||"+\\infty"}} ${v.expr||"f(x)"}`,
  },
  {
    id: "int",
    label: "Intégrale définie",
    icon: "∫",
    fields: [
      { id: "a",    label: "Borne inf.",   placeholder: "ex: 0" },
      { id: "b",    label: "Borne sup.",   placeholder: "ex: 1" },
      { id: "f",    label: "Intégrande",   placeholder: "ex: f(x)" },
      { id: "dx",   label: "Variable",     placeholder: "ex: x" },
    ],
    latex: (v) => `\\int_{${v.a||"a"}}^{${v.b||"b"}} ${v.f||"f(x)"} \\, d${v.dx||"x"}`,
  },
  {
    id: "sum",
    label: "Somme (Σ)",
    icon: "Σ",
    fields: [
      { id: "i",    label: "Indice",        placeholder: "ex: i=1" },
      { id: "n",    label: "Borne sup.",    placeholder: "ex: n" },
      { id: "expr", label: "Expression",   placeholder: "ex: a_i" },
    ],
    latex: (v) => `\\sum_{${v.i||"i=1"}}^{${v.n||"n"}} ${v.expr||"a_i"}`,
  },
  {
    id: "prod",
    label: "Produit (Π)",
    icon: "Π",
    fields: [
      { id: "i",    label: "Indice",        placeholder: "ex: i=1" },
      { id: "n",    label: "Borne sup.",    placeholder: "ex: n" },
      { id: "expr", label: "Expression",   placeholder: "ex: a_i" },
    ],
    latex: (v) => `\\prod_{${v.i||"i=1"}}^{${v.n||"n"}} ${v.expr||"a_i"}`,
  },
  {
    id: "vec",
    label: "Vecteur",
    icon: "→",
    fields: [
      { id: "x", label: "Nom",        placeholder: "ex: AB" },
    ],
    latex: (v) => `\\overrightarrow{${v.x||"AB"}}`,
  },
  {
    id: "matrix2",
    label: "Matrice 2×2",
    icon: "[]",
    fields: [
      { id: "a", label: "a (ligne 1, col 1)", placeholder: "a" },
      { id: "b", label: "b (ligne 1, col 2)", placeholder: "b" },
      { id: "c", label: "c (ligne 2, col 1)", placeholder: "c" },
      { id: "d", label: "d (ligne 2, col 2)", placeholder: "d" },
    ],
    latex: (v) => `\\begin{pmatrix}${v.a||"a"} & ${v.b||"b"} \\\\ ${v.c||"c"} & ${v.d||"d"}\\end{pmatrix}`,
  },
];

// ── Symbol categories ────────────────────────────────
const SYMBOL_CATS = [
  { id: "grec",    label: "Grec",       icon: "α",  groups: [
    { label: "Minuscules", syms: ["α","β","γ","δ","ε","ζ","η","θ","ι","κ","λ","μ","ν","ξ","π","ρ","σ","τ","υ","φ","χ","ψ","ω"] },
    { label: "Majuscules", syms: ["Γ","Δ","Θ","Λ","Ξ","Π","Σ","Φ","Ψ","Ω"] },
  ]},
  { id: "ops",     label: "Opérat.",    icon: "±",  groups: [
    { label: "Arithmétique",          syms: ["×","÷","±","∓","·","∘","∗","⊕","⊗"] },
    { label: "Exposants / Indices",   syms: ["²","³","⁴","⁵","ⁿ","⁻¹","₀","₁","₂","₃","₄","₅","ₙ"] },
    { label: "Fractions courantes",   syms: ["½","⅓","⅔","¼","¾","⅕","⅖","⅗","⅘"] },
    { label: "Radicaux / Sommes",     syms: ["√","∛","∜","∑","∏","∞"] },
  ]},
  { id: "rel",     label: "Relations",  icon: "≤",  groups: [
    { label: "Comparaisons",          syms: ["=","≠","≈","≡","≜","<",">","≤","≥","≪","≫"] },
    { label: "Proportionnalité",      syms: ["∝","∼","≅","≃","∴","∵"] },
  ]},
  { id: "calc",    label: "Calcul",     icon: "∫",  groups: [
    { label: "Intégrales / Dérivées", syms: ["∫","∬","∭","∮","∂","∇","Δ","δ"] },
    { label: "Fonctions",             syms: ["sin","cos","tan","ln","log","exp","lim","max","min","mod"] },
    { label: "Mesures",               syms: ["°","′","″","℃","Å"] },
  ]},
  { id: "ens",     label: "Ensembles",  icon: "∈",  groups: [
    { label: "Relations",             syms: ["∈","∉","⊂","⊃","⊆","⊇","∪","∩","∖","∅"] },
    { label: "Corps de nombres",      syms: ["ℕ","ℤ","ℚ","ℝ","ℂ"] },
    { label: "Quantificateurs",       syms: ["∀","∃","∄","¬","∧","∨","⊢","⊨"] },
  ]},
  { id: "geo",     label: "Géométrie",  icon: "∠",  groups: [
    { label: "Formes / Angles",       syms: ["∠","∡","⊥","∥","∦","△","▽","□","⊙","○"] },
    { label: "Congruence",            syms: ["≅","≈","∼","⟂"] },
  ]},
  { id: "arrows",  label: "Flèches",    icon: "→",  groups: [
    { label: "Simples",               syms: ["→","←","↑","↓","↔","↗","↘","↙","↖","↩","↪"] },
    { label: "Doubles",               syms: ["⇒","⇐","⇔","⟹","⟺","⟶","⟵"] },
  ]},
];

// ── Main component ───────────────────────────────────
export default function MathKeyboard({ onInsert, onClose = null, insertLabel = "Insérer dans le message" }) {
  const [mainTab,    setMainTab]    = useState("build");   // "build" | "symbols"
  const [activeTpl,  setActiveTpl]  = useState("frac");
  const [tplValues,  setTplValues]  = useState({});
  const [symCat,     setSymCat]     = useState("grec");
  const [latexInput, setLatexInput] = useState("");
  const inputRefs = useRef({});

  const tpl = TEMPLATES.find(t => t.id === activeTpl) || TEMPLATES[0];
  const vals = tplValues[activeTpl] || {};
  const generatedLatex = tpl.latex(vals);

  const setField = (fieldId, value) => {
    setTplValues(prev => ({
      ...prev,
      [activeTpl]: { ...(prev[activeTpl] || {}), [fieldId]: value },
    }));
  };

  const insertExpr = () => {
    const latex = latexInput.trim() ? latexInput.trim() : generatedLatex;
    onInsert(`$${latex}$`);
    setLatexInput("");
  };

  const symGroup = SYMBOL_CATS.find(c => c.id === symCat);

  return (
    <div className="ql-mathkb">
      {/* ── Header ── */}
      <div className="ql-mathkb-header">
        <div className="ql-mathkb-main-tabs">
          <button
            className={`ql-mathkb-main-tab${mainTab === "build" ? " active" : ""}`}
            onMouseDown={e => { e.preventDefault(); setMainTab("build"); }}
          >
            <i className="bi bi-pencil-square me-1"></i>Construire
          </button>
          <button
            className={`ql-mathkb-main-tab${mainTab === "symbols" ? " active" : ""}`}
            onMouseDown={e => { e.preventDefault(); setMainTab("symbols"); }}
          >
            <i className="bi bi-grid me-1"></i>Symboles
          </button>
          <button
            className={`ql-mathkb-main-tab${mainTab === "latex" ? " active" : ""}`}
            onMouseDown={e => { e.preventDefault(); setMainTab("latex"); }}
          >
            <i className="bi bi-code-slash me-1"></i>LaTeX
          </button>
        </div>
        {onClose && (
          <button className="ql-mathkb-close" onMouseDown={e => { e.preventDefault(); onClose(); }}>
            <i className="bi bi-x-lg"></i>
          </button>
        )}
      </div>

      {/* ══ TAB: Construire ══════════════════════════════════ */}
      {mainTab === "build" && (
        <div className="ql-mathkb-build">
          {/* Template selector */}
          <div className="ql-mathkb-tpl-list">
            {TEMPLATES.map(t => (
              <button
                key={t.id}
                className={`ql-mathkb-tpl-btn${activeTpl === t.id ? " active" : ""}`}
                onMouseDown={e => { e.preventDefault(); setActiveTpl(t.id); setTplValues(v => ({ ...v, [t.id]: {} })); }}
              >
                <span className="ql-mathkb-tpl-icon">{t.icon}</span>
                <span className="ql-mathkb-tpl-label">{t.label}</span>
              </button>
            ))}
          </div>

          {/* Fields + preview */}
          <div className="ql-mathkb-build-right">
            <div className="ql-mathkb-fields">
              {tpl.fields.map(f => (
                <div key={f.id} className="ql-mathkb-field">
                  <label>{f.label}</label>
                  <input
                    ref={el => (inputRefs.current[f.id] = el)}
                    type="text"
                    placeholder={f.placeholder}
                    value={vals[f.id] || ""}
                    onChange={e => setField(f.id, e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); insertExpr(); } }}
                  />
                </div>
              ))}
            </div>

            <div className="ql-mathkb-preview">
              <div className="ql-mathkb-preview-label">Prévisualisation</div>
              <div className="ql-mathkb-preview-box">
                <Preview latex={generatedLatex} />
              </div>
            </div>

            <button
              className="btn btn-primary btn-sm w-100 mt-2"
              onMouseDown={e => { e.preventDefault(); insertExpr(); }}
            >
              <i className="bi bi-send me-1"></i>{insertLabel}
            </button>
          </div>
        </div>
      )}

      {/* ══ TAB: Symboles ════════════════════════════════════ */}
      {mainTab === "symbols" && (
        <div className="ql-mathkb-sym-panel">
          <div className="ql-mathkb-tabs">
            {SYMBOL_CATS.map(c => (
              <button
                key={c.id}
                className={`ql-mathkb-tab${symCat === c.id ? " active" : ""}`}
                onMouseDown={e => { e.preventDefault(); setSymCat(c.id); }}
              >
                <span className="ql-mathkb-tab-icon">{c.icon}</span>
                <span className="ql-mathkb-tab-label">{c.label}</span>
              </button>
            ))}
          </div>
          <div className="ql-mathkb-body">
            {symGroup?.groups.map(g => (
              <div key={g.label} className="ql-mathkb-group">
                <div className="ql-mathkb-group-label">{g.label}</div>
                <div className="ql-mathkb-syms">
                  {g.syms.map(s => (
                    <button
                      key={s}
                      className="ql-mathkb-sym"
                      title={s}
                      onMouseDown={e => { e.preventDefault(); onInsert(s); }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══ TAB: LaTeX direct ════════════════════════════════ */}
      {mainTab === "latex" && (
        <div className="ql-mathkb-latex-panel">
          <div className="ql-mathkb-latex-info">
            Entrez du LaTeX directement. L'expression sera entourée de <code>$...$</code> dans le message.
          </div>
          <div className="ql-mathkb-latex-row">
            <input
              className="ql-mathkb-latex-in"
              type="text"
              placeholder="\frac{1}{2}, x^{2}, \sqrt{x}, …"
              value={latexInput}
              onChange={e => setLatexInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); if (latexInput.trim()) { onInsert(`$${latexInput.trim()}$`); setLatexInput(""); } } }}
            />
            <button
              className="btn btn-primary btn-sm"
              onMouseDown={e => { e.preventDefault(); if (latexInput.trim()) { onInsert(`$${latexInput.trim()}$`); setLatexInput(""); } }}
            >
              <i className="bi bi-send"></i>
            </button>
          </div>
          {latexInput.trim() && (
            <div className="ql-mathkb-preview-box mt-2">
              <Preview latex={latexInput} />
            </div>
          )}
          <div className="ql-mathkb-latex-examples">
            <div className="ql-mathkb-group-label">Exemples rapides</div>
            <div className="ql-mathkb-syms">
              {[
                ["\\dfrac{a}{b}", "a/b"],
                ["x^{2}", "x²"],
                ["\\sqrt{x}", "√x"],
                ["\\log_{2}(8)", "log₂"],
                ["e^{x}", "eˣ"],
                ["\\int_{a}^{b}", "∫"],
                ["\\lim_{x \\to 0}", "lim"],
                ["\\sum_{i=1}^{n}", "Σ"],
              ].map(([latex, label]) => (
                <button
                  key={latex}
                  className="ql-mathkb-sym"
                  style={{ minWidth: 52, fontSize: "0.7rem", fontFamily: "monospace", height: "auto", padding: "4px 6px" }}
                  title={latex}
                  onMouseDown={e => { e.preventDefault(); setLatexInput(latex); }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
